import dayjs from 'dayjs'
import isNil from 'lodash/isNil'
import type { UserListRow } from '../../api/dto/user-invitation.dto'
import {
  downloadExportWorkbook,
  type ExportFormat,
  type ExportScopeOption,
} from '../ClientBillingProfileManagement/bulkUpload.excel'
import { getDisplayableRbacRoles, getDisplayStatusConfig } from './userDisplay'

const SHEET_NAME = 'Finance managers'
const FILE_STEM = 'finance-managers'

const HEADERS = [
  'Email',
  'Name',
  'Finance Manager',
  'Role',
  'Companies',
  'Invited',
  'Registered date',
  'Last portal access',
  'Last invoice download',
] as const

const COLUMN_WIDTHS = [
  { wch: 34 },
  { wch: 24 },
  { wch: 22 },
  { wch: 20 },
  { wch: 44 },
  { wch: 14 },
  { wch: 14 },
  { wch: 18 },
  { wch: 20 },
]

/**
 * A blank cell for a missing date rather than the table's dash, which a
 * spreadsheet would treat as text to be filtered and sorted.
 */
const exportDate = (iso: string | null): string =>
  isNil(iso) ? '' : dayjs(iso).format('MM/DD/YYYY')

/**
 * One row per person. The Finance Manager cell holds the same wording as the
 * column on screen, so the file and the table answer "are they invited"
 * identically.
 */
export const buildRosterRows = (
  rows: UserListRow[]
): Record<string, string>[] =>
  rows.map((row) => ({
    Email: row.email,
    Name: row.fullName ?? '',
    'Finance Manager': getDisplayStatusConfig(row).label,
    Role: getDisplayableRbacRoles(row.rbacRoles)
      .map((role) => role.name)
      .join('; '),
    Companies: row.billingClientNames.join('; '),
    Invited: exportDate(row.createdAt),
    'Registered date': exportDate(row.activatedAt),
    'Last portal access': exportDate(row.lastAccessAt),
    'Last invoice download': exportDate(row.lastInvoiceDownloadAt),
  }))

export const downloadRosterExport = (
  rows: UserListRow[],
  format: ExportFormat
) =>
  downloadExportWorkbook(
    [
      {
        name: SHEET_NAME,
        rows: buildRosterRows(rows),
        header: [...HEADERS],
        widths: COLUMN_WIDTHS,
      },
    ],
    FILE_STEM,
    format
  )

/**
 * The roster's only scope. Stated as a filtered export because it is: the file
 * holds what the table is showing, which is the point of exporting from here.
 */
export const ROSTER_EXPORT_SCOPES: ExportScopeOption<'FUNDING_MANAGERS'>[] = [
  {
    value: 'FUNDING_MANAGERS',
    label: 'Finance managers',
    hint: 'One row per person, matching the filters on this page.',
  },
]
